import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Plus, Search, SlidersHorizontal, X } from "lucide-react";
import type { VelaWorkspace } from "@luxalgo/vela/workspace";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  TRADE_LOG_FILTER_INPUT_CLASS,
  TRADE_LOG_FILTER_SELECT_CONTENT_CLASS,
  TRADE_LOG_FILTER_SELECT_ITEM_CLASS,
  TRADE_LOG_FILTER_SELECT_TRIGGER_CLASS,
} from "@/components/trade-filter-selects";
import { cn } from "@/lib/utils";

/**
 * Strategy Lab chart controls (symbol / timeframe / indicators) drawn with the app's own
 * components, portaled into the Vela top bar where the library's controls used to sit.
 * Every change is applied through the workspace's ACTIVE cell — the same calls the
 * library's own pickers make — so `market:changed` and the indicator ledger behave as before.
 */

/** Above the full-page chart (z 1400) and the chart's ask menu. */
const LAB_OVERLAY_Z = "z-[1500]";

const DIALOG_TITLE_CLASS =
  "font-[family-name:var(--font-primary)] text-base font-normal text-[color:var(--brand-tropical-surf)]";
const BODY_TEXT_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-white";
const SECTION_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]";
const PANEL_CLASS =
  "overflow-hidden rounded-[6px] !border !border-solid !border-border";
const HEAD_CLASS =
  "!h-8 !min-h-8 !max-h-8 !px-3 font-[family-name:var(--font-primary)] text-xs font-normal !text-gray-500 dark:!text-white";
const CELL_CLASS = "!px-3 !py-1.5";
/** Toolbar triggers match the Vela top bar's 32px controls. */
// Sentence case like the Lab's other buttons (the base Button is uppercase IBM Plex).
const TOOLBAR_BUTTON_CLASS = "!h-8 rounded-[6px] !px-2.5 gap-1.5 font-[family-name:var(--font-primary)] !normal-case !tracking-normal";

/** Asset class → the tab's name ("fx" → "Forex"); unknown classes are title-cased. */
const ASSET_TYPE_LABELS: Record<string, string> = {
  fx: "Forex",
  forex: "Forex",
  crypto: "Crypto",
  index: "Indices",
  indices: "Indices",
  equity: "Stocks",
  stock: "Stocks",
  stocks: "Stocks",
  commodity: "Commodities",
  commodities: "Commodities",
  energy: "Energy",
  metal: "Metals",
  metals: "Metals",
  bond: "Bonds",
  bonds: "Bonds",
  etf: "ETFs",
  future: "Futures",
  futures: "Futures",
};
function assetTypeLabel(t: string): string {
  const k = t.trim().toLowerCase();
  return ASSET_TYPE_LABELS[k] ?? k.replace(/(^|[\s_-])([a-z])/g, (_m, a: string, b: string) => `${a === "_" || a === "-" ? " " : a}${b.toUpperCase()}`);
}

/** Row actions in the dialogs: the Lab's standard 32px button, never squeezed. */
const DIALOG_ACTION_CLASS = "!h-8 shrink-0 gap-1.5 !rounded-[6px] !px-3 shadow-none";

const VELA_TF_LABELS: Record<string, string> = {
  "1": "1m",
  "5": "5m",
  "15": "15m",
  "30": "30m",
  "60": "1h",
  "240": "4h",
  "1D": "1D",
  D: "1D",
};

export function velaTimeframeLabel(tf: string): string {
  if (VELA_TF_LABELS[tf]) return VELA_TF_LABELS[tf]!;
  const n = Number(tf);
  if (Number.isFinite(n) && n > 0) {
    return n % 60 === 0 ? `${n / 60}h` : `${n}m`;
  }
  return tf;
}

export type StrategyRdChartToolbarSymbol = {
  ticker: string;
  description?: string;
  type?: string;
};

type IndicatorRow = { name: string; native?: boolean; language?: string; category?: string; beta?: boolean };

export type StrategyRdChartToolbarProps = {
  /** Live workspace (null while unmounted). Read at the point of use, never cached. */
  getWorkspace: () => VelaWorkspace | null;
  /** Bare ticker shown on the symbol control. */
  symbol: string;
  /** Vela timeframe (e.g. `15`, `60`). */
  timeframe: string;
  /** Vela timeframes the dropdown offers (same allowlist the library chips used). */
  timeframes: readonly string[];
  /** False when the timeframe is locked: the dropdown is not shown (as the chips were not). */
  showTimeframe: boolean;
  /** The instrument cannot change: symbol control is display only. */
  symbolLocked: boolean;
  symbols: readonly StrategyRdChartToolbarSymbol[];
  onPickSymbol: (ticker: string) => void;
  onPickTimeframe: (tf: string) => void;
  /** Bare letter typed on the chart: open the symbol search seeded with it. */
  symbolSearchRequest?: { query: string; seq: number } | null;
};

/**
 * The slice of Vela's `ChartCell` (`@luxalgo/vela/dist/workspace.d.ts`) this toolbar drives.
 * The app's ambient shim (`src/types/luxalgo-vela-workspace.d.ts`) types `ws.active` with
 * only the crosshair fields, so the cell is read through this structural type.
 */
export type LabChartCell = {
  setSymbol: (symbol: string) => void;
  setTimeframe: (timeframe: string) => void;
  onChartRows: () => IndicatorRow[];
  libraryRows: () => IndicatorRow[];
  addFromLibrary: (index: number) => void;
  removeFromChart: (index: number) => void;
};

/** `ws.active` throws after destroy: never call it unguarded. */
export function labActiveCell(ws: VelaWorkspace | null | undefined): LabChartCell | null {
  if (!ws) return null;
  try {
    return (ws.active as unknown as LabChartCell | undefined) ?? null;
  } catch {
    return null;
  }
}

/** A library dialog (settings, its own pickers) holds the keyboard. */
export function labLibraryDialogOpen(ws: VelaWorkspace | null | undefined): boolean {
  try {
    const keymap = (ws as unknown as { keymap?: { activeScope?: string } } | null)?.keymap;
    return keymap?.activeScope === "dialog";
  } catch {
    return true;
  }
}

const activeCell = labActiveCell;

function readRows(ws: VelaWorkspace | null): { onChart: IndicatorRow[]; library: IndicatorRow[] } {
  const cell = activeCell(ws);
  if (!cell) return { onChart: [], library: [] };
  try {
    return { onChart: cell.onChartRows(), library: cell.libraryRows() };
  } catch {
    return { onChart: [], library: [] };
  }
}

const StrategyRdChartToolbar: React.FC<StrategyRdChartToolbarProps> = ({
  getWorkspace,
  symbol,
  timeframe,
  timeframes,
  showTimeframe,
  symbolLocked,
  symbols,
  onPickSymbol,
  onPickTimeframe,
  symbolSearchRequest = null,
}) => {
  // Optimistic until the host's props catch up with the pick (the Lab refetches first).
  const [pendingTf, setPendingTf] = useState<string | null>(null);
  const [pendingSymbol, setPendingSymbol] = useState<string | null>(null);
  useEffect(() => setPendingTf(null), [timeframe]);
  useEffect(() => setPendingSymbol(null), [symbol]);
  const shownTf = pendingTf ?? timeframe;
  const shownSymbol = pendingSymbol ?? symbol;

  // ---- Symbol search -------------------------------------------------------------------
  const [symbolOpen, setSymbolOpen] = useState(false);
  const [symbolQuery, setSymbolQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  // Asset-type tabs (All, Forex, Crypto…) from the symbols' own types, in first-seen order.
  const [symbolType, setSymbolType] = useState("all");
  const symbolTypes = useMemo(() => {
    const seen: string[] = [];
    for (const s of symbols) {
      const t = (s.type || "").trim();
      if (t && !seen.includes(t)) seen.push(t);
    }
    return seen;
  }, [symbols]);
  const lastRequestSeq = useRef<number | null>(null);

  useEffect(() => {
    if (!symbolSearchRequest || symbolLocked) return;
    if (lastRequestSeq.current === symbolSearchRequest.seq) return;
    lastRequestSeq.current = symbolSearchRequest.seq;
    setSymbolQuery((prev) => (symbolOpen ? prev + symbolSearchRequest.query : symbolSearchRequest.query));
    setHighlight(0);
    setSymbolOpen(true);
  }, [symbolLocked, symbolOpen, symbolSearchRequest]);

  const symbolResults = useMemo(() => {
    const q = symbolQuery.trim().toUpperCase();
    const ofType = symbolType === "all" ? symbols : symbols.filter((s) => (s.type || "").trim() === symbolType);
    const list = q
      ? ofType.filter(
          (s) =>
            s.ticker.toUpperCase().includes(q) ||
            (s.description ?? "").toUpperCase().includes(q)
        )
      : [...ofType];
    // Ticker prefix matches first, then the rest in provider order.
    if (q) {
      list.sort((a, b) => {
        const ap = a.ticker.toUpperCase().startsWith(q) ? 0 : 1;
        const bp = b.ticker.toUpperCase().startsWith(q) ? 0 : 1;
        return ap - bp;
      });
    }
    return list;
  }, [symbolQuery, symbolType, symbols]);

  const pickSymbol = useCallback(
    (ticker: string) => {
      setSymbolOpen(false);
      setSymbolQuery("");
      if (ticker.toUpperCase() !== symbol.toUpperCase()) setPendingSymbol(ticker);
      onPickSymbol(ticker);
    },
    [onPickSymbol, symbol]
  );

  const onSymbolKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(symbolResults.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      const hit = symbolResults[highlight];
      if (hit) {
        e.preventDefault();
        pickSymbol(hit.ticker);
      }
    }
  };

  // ---- Indicators ------------------------------------------------------------------------
  const [indicatorsOpen, setIndicatorsOpen] = useState(false);
  const [indicatorQuery, setIndicatorQuery] = useState("");
  const [rows, setRows] = useState<{ onChart: IndicatorRow[]; library: IndicatorRow[] }>({
    onChart: [],
    library: [],
  });
  const refreshRows = useCallback(() => setRows(readRows(getWorkspace())), [getWorkspace]);

  // The ledger changes outside this dialog too (Lab chips, legend close buttons, undo):
  // follow the workspace's debounced state signal.
  useEffect(() => {
    const ws = getWorkspace();
    refreshRows();
    if (!ws) return;
    let off: (() => void) | null = null;
    try {
      off = ws.on("state:changed", refreshRows);
    } catch {
      off = null;
    }
    return () => {
      try {
        off?.();
      } catch {
        // destroyed
      }
    };
  }, [getWorkspace, refreshRows]);

  useEffect(() => {
    if (indicatorsOpen) refreshRows();
  }, [indicatorsOpen, refreshRows]);

  const afterLedgerChange = useCallback(() => {
    refreshRows();
    // Script instances can land a frame later; read again once they settle.
    requestAnimationFrame(() => refreshRows());
  }, [refreshRows]);

  const removeIndicator = (index: number) => {
    const cell = activeCell(getWorkspace());
    if (!cell) return;
    try {
      cell.removeFromChart(index);
    } catch {
      // ignore
    }
    afterLedgerChange();
  };

  const addIndicator = (libraryIndex: number) => {
    const cell = activeCell(getWorkspace());
    if (!cell) return;
    try {
      cell.addFromLibrary(libraryIndex);
    } catch {
      // ignore
    }
    afterLedgerChange();
  };

  // Library index must be the ORIGINAL row index (addFromLibrary's index space).
  const libraryResults = useMemo(() => {
    const q = indicatorQuery.trim().toLowerCase();
    return rows.library
      .map((row, index) => ({ row, index }))
      .filter(({ row }) =>
        q
          ? row.name.toLowerCase().includes(q) || (row.category ?? "").toLowerCase().includes(q)
          : true
      );
  }, [indicatorQuery, rows.library]);

  const tfOptions = useMemo(
    () => timeframes.map((tf) => ({ value: tf, label: velaTimeframeLabel(tf) })),
    [timeframes]
  );
  const tfValue = tfOptions.some((o) => o.value === shownTf) ? shownTf : undefined;

  return (
    <div className="flex items-center gap-1.5" data-strategy-rd-chart-toolbar="">
      <Button
        data-font="primary"
        type="button"
        variant="outline-brand"
        size="xs"
        className={TOOLBAR_BUTTON_CLASS}
        disabled={symbolLocked}
        onClick={() => {
          setSymbolQuery("");
          setHighlight(0);
          setSymbolOpen(true);
        }}
        aria-label="Search symbol"
        data-strategy-rd-chart-symbol=""
      >
        <Search aria-hidden />
        <span>{shownSymbol || "Symbol"}</span>
      </Button>

      {showTimeframe && tfOptions.length > 0 ? (
        <Select
          value={tfValue}
          onValueChange={(next) => {
            if (!next || next === shownTf) return;
            setPendingTf(next);
            onPickTimeframe(next);
          }}
        >
          <SelectTrigger
            aria-label="Timeframe"
            data-trade-details-journal-control=""
            data-strategy-rd-chart-timeframe=""
            className={cn("!h-8 w-fit min-w-[4.5rem]", TRADE_LOG_FILTER_SELECT_TRIGGER_CLASS)}
          >
            <SelectValue placeholder="Timeframe" />
          </SelectTrigger>
          <SelectContent
            position="popper"
            fitToContent
            data-trade-log-filter-select-content=""
            className={cn(LAB_OVERLAY_Z, TRADE_LOG_FILTER_SELECT_CONTENT_CLASS)}
          >
            {tfOptions.map((o) => (
              <SelectItem
                key={o.value}
                value={o.value}
                className={cn("whitespace-nowrap", TRADE_LOG_FILTER_SELECT_ITEM_CLASS)}
              >
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}

      <Button
        data-font="primary"
        type="button"
        variant="outline-brand"
        size="xs"
        className={TOOLBAR_BUTTON_CLASS}
        onClick={() => setIndicatorsOpen(true)}
        data-strategy-rd-chart-indicators=""
      >
        <SlidersHorizontal aria-hidden />
        <span className="normal-case tracking-normal">
          Indicators{rows.onChart.length > 0 ? ` (${rows.onChart.length})` : ""}
        </span>
        <ChevronDown aria-hidden className="opacity-70" />
      </Button>

      {/* Symbol search */}
      <Dialog open={symbolOpen} onOpenChange={setSymbolOpen}>
        <DialogContent
          data-strategy-rd-chart-symbol-dialog=""
          stackLayer="top"
          overlayClassName={LAB_OVERLAY_Z}
          className={cn(
            LAB_OVERLAY_Z,
            "flex max-h-[min(80vh,36rem)] flex-col gap-3 !border !border-solid !border-border sm:max-w-2xl"
          )}
        >
          <DialogHeader className="shrink-0 gap-1.5 text-left">
            <DialogTitle className={DIALOG_TITLE_CLASS}>Symbol search</DialogTitle>
            <DialogDescription className={BODY_TEXT_CLASS}>
              Pick an instrument to load on the chart.
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            value={symbolQuery}
            onChange={(e) => {
              setSymbolQuery(e.target.value);
              setHighlight(0);
            }}
            onKeyDown={onSymbolKeyDown}
            placeholder="Search symbols"
            aria-label="Search symbols"
            className={cn("shrink-0", TRADE_LOG_FILTER_INPUT_CLASS)}
          />
          {symbolTypes.length > 1 ? (
            <Tabs
              data-trade-log-tabs=""
              value={symbolType}
              onValueChange={(v) => {
                setSymbolType(v);
                setHighlight(0);
              }}
              className="shrink-0 !mb-0"
            >
              {/* Scrolls sideways when there are many types, without a visible scrollbar. */}
              <div className="max-w-full overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <TabsList className="min-w-max">
                  <TabsTrigger value="all">All</TabsTrigger>
                  {symbolTypes.map((t) => (
                    <TabsTrigger key={t} value={t}>
                      {assetTypeLabel(t)}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
            </Tabs>
          ) : null}
          <div className={cn(PANEL_CLASS, "min-h-0 flex-1 overflow-y-auto")}>
            {symbolResults.length === 0 ? (
              <p className={cn(BODY_TEXT_CLASS, "!px-3 !py-3")}>No symbols match.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className={HEAD_CLASS}>Symbol</TableHead>
                    <TableHead className={HEAD_CLASS}>Description</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {symbolResults.map((s, i) => {
                    const current = s.ticker.toUpperCase() === (pendingSymbol ?? symbol).toUpperCase();
                    return (
                      <TableRow
                        key={s.ticker}
                        data-state={i === highlight ? "selected" : undefined}
                        className="cursor-pointer"
                        onMouseEnter={() => setHighlight(i)}
                        onClick={() => pickSymbol(s.ticker)}
                      >
                        <TableCell
                          className={cn(
                            CELL_CLASS,
                            "font-[family-name:var(--font-primary)] text-sm",
                            current
                              ? "text-[color:var(--brand-tropical-surf)]"
                              : "text-gray-500 dark:text-white"
                          )}
                        >
                          {s.ticker}
                        </TableCell>
                        <TableCell className={cn(CELL_CLASS, BODY_TEXT_CLASS, "truncate")}>
                          {s.description || s.type || ""}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Indicators */}
      <Dialog
        open={indicatorsOpen}
        onOpenChange={(open) => {
          setIndicatorsOpen(open);
          if (!open) setIndicatorQuery("");
        }}
      >
        <DialogContent
          data-strategy-rd-chart-indicators-dialog=""
          stackLayer="top"
          overlayClassName={LAB_OVERLAY_Z}
          className={cn(
            LAB_OVERLAY_Z,
            "flex max-h-[min(85vh,40rem)] flex-col gap-4 !border !border-solid !border-border sm:max-w-lg"
          )}
        >
          <DialogHeader className="shrink-0 gap-1.5 text-left">
            <DialogTitle className={DIALOG_TITLE_CLASS}>Indicators</DialogTitle>
            <DialogDescription className={BODY_TEXT_CLASS}>
              Remove what is on the chart, or add from the library.
            </DialogDescription>
          </DialogHeader>

          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pe-1">
            <section className="flex flex-col gap-1.5" data-strategy-rd-chart-indicators-on-chart="">
              <h3 className={SECTION_LABEL_CLASS}>On the chart</h3>
              <div className={PANEL_CLASS}>
                {rows.onChart.length === 0 ? (
                  <p className={cn(BODY_TEXT_CLASS, "!px-3 !py-3")}>No indicators on the chart.</p>
                ) : (
                  <Table>
                    <TableBody>
                      {rows.onChart.map((row, index) => (
                        <TableRow key={`${row.name}-${index}`}>
                          <TableCell className={cn(CELL_CLASS, BODY_TEXT_CLASS, "w-full")}>
                            {row.name}
                          </TableCell>
                          <TableCell className={cn(CELL_CLASS, "text-right")}>
                            <Button
        data-font="primary"
                              type="button"
                              variant="outline-brand"
                              size="sm"
                              className={DIALOG_ACTION_CLASS}
                              onClick={() => removeIndicator(index)}
                              aria-label={`Remove ${row.name}`}
                            >
                              <X className="size-3.5" aria-hidden />
                              <span className="normal-case tracking-normal">Remove</span>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </section>

            <section className="flex flex-col gap-1.5" data-strategy-rd-chart-indicators-library="">
              <h3 className={SECTION_LABEL_CLASS}>Add</h3>
              <Input
                value={indicatorQuery}
                onChange={(e) => setIndicatorQuery(e.target.value)}
                placeholder="Search indicators"
                aria-label="Search indicators"
                className={TRADE_LOG_FILTER_INPUT_CLASS}
              />
              <div className={PANEL_CLASS}>
                {libraryResults.length === 0 ? (
                  <p className={cn(BODY_TEXT_CLASS, "!px-3 !py-3")}>No indicators match.</p>
                ) : (
                  <Table>
                    <TableBody>
                      {libraryResults.map(({ row, index }) => (
                        <TableRow key={`${row.name}-${index}`}>
                          <TableCell className={cn(CELL_CLASS, BODY_TEXT_CLASS, "w-full")}>
                            {row.name}
                            {row.beta ? <span className="ms-1 text-xs opacity-70">beta</span> : null}
                          </TableCell>
                          <TableCell className={cn(CELL_CLASS, "text-right")}>
                            <Button
        data-font="primary"
                              type="button"
                              variant="gradient"
                              size="sm"
                              className={DIALOG_ACTION_CLASS}
                              onClick={() => addIndicator(index)}
                              aria-label={`Add ${row.name}`}
                            >
                              <Plus className="size-3.5" aria-hidden />
                              <span className="normal-case tracking-normal">Add</span>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </section>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StrategyRdChartToolbar;
